<template>
    <div v-click-outside="() => (expandOptions = false)" class="relative">
        <SearchInput
            ref="inputField"
            v-model:value="computedSearch"
            :label="label"
            prevent-esc-propagation
            @submit="onSearchSubmit"
            @on-escape="onEscape"
            @on-tab="expandOptions = false"
            @focus="expandOptions = true"
        ></SearchInput>
        <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-100 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <!-- Virtualized list for large datasets -->
            <div v-if="expandOptions && isLargeList" ref="virtualListRef" :class="[dropdownHeightClass]" class="absolute top-full left-0 w-full bg-white border z-30 rounded overflow-y-auto">
                <div :style="{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }">
                    <div
                        v-for="virtualRow in virtualizer.getVirtualItems()"
                        :key="filteredItems[virtualRow.index][itemValue]"
                        class="hover:bg-gray-100 cursor-pointer absolute left-0 w-full box-border"
                        :style="{
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                        }"
                        @mousedown="onItemClicked(filteredItems[virtualRow.index])"
                    >
                        <span class="p-3 w-full block truncate" v-html="getBoldenedString(filteredItems[virtualRow.index][itemText])"></span>
                    </div>
                </div>
            </div>
            <!-- Simple list for small datasets -->
            <div v-else-if="expandOptions" :class="[dropdownHeightClass]" class="absolute top-full left-0 w-full bg-white border z-30 rounded overflow-y-auto">
                <div v-for="item of filteredItems" :key="item[itemValue]" class="hover:bg-gray-100 cursor-pointer" @mousedown="onItemClicked(item)">
                    <span class="p-3 w-full block truncate" v-html="getBoldenedString(item[itemText])"></span>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useVirtualizer } from '@tanstack/vue-virtual';
import sanitizeHtml from 'sanitize-html';
import ClickOutside from '@makeabledk/vue-ui/directives/click-outside';
import SearchInput from '@/components/ui/SearchInput.vue';

const vClickOutside = ClickOutside;

const LARGE_LIST_THRESHOLD = 200;
const ITEM_HEIGHT = 44;

const props = withDefaults(
    defineProps<{
        label: string;
        items?: any[];
        itemText: string;
        itemValue: string;
        dropdownHeightClass?: string;
        search?: string | null;
    }>(),
    {
        items: () => [],
        dropdownHeightClass: 'max-h-64',
        search: null,
    }
);

const emit = defineEmits<{
    'update:value': [value: any];
    'update:search': [value: string];
    'item-clicked': [item: any];
}>();

const expandOptions = ref(false);
const localSearch = ref('');
const inputField = ref<InstanceType<typeof SearchInput> | null>(null);
const virtualListRef = ref<HTMLElement | null>(null);

const isLargeList = computed(() => props.items.length > LARGE_LIST_THRESHOLD);

const computedSearch = computed({
    get(): string {
        return props.search === null ? localSearch.value : props.search;
    },
    set(newValue: string) {
        if (props.search === null) {
            localSearch.value = newValue;
        } else {
            emit('update:search', newValue);
        }
    },
});

const debouncedSearchValue = refDebounced(
    computed(() => computedSearch.value),
    computed(() => (isLargeList.value ? 200 : 0))
);

const searchValueForFiltering = computed(() => (isLargeList.value ? debouncedSearchValue.value : computedSearch.value));

const searchRegex = computed(() => {
    const searchStr = searchValueForFiltering.value.trim();
    if (!searchStr) return null;
    const escaped = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(${escaped})`, 'gi');
});

const filteredItems = computed(() => {
    const searchString = searchValueForFiltering.value.trim().toLowerCase();
    if (!searchString) {
        return props.items;
    }
    return props.items.filter((item: any) => {
        const itemTextValue = item[props.itemText];
        if (typeof itemTextValue !== 'string') return false;
        return itemTextValue.trim().toLowerCase().includes(searchString);
    });
});

const virtualizer = useVirtualizer({
    get count() {
        return filteredItems.value.length;
    },
    getScrollElement: () => virtualListRef.value,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
});

watch(filteredItems, () => {
    if (virtualListRef.value) {
        virtualListRef.value.scrollTop = 0;
    }
});

const sanitizedTextCache = new Map<string, string>();

function getSanitizedText(text: string): string {
    if (sanitizedTextCache.has(text)) {
        return sanitizedTextCache.get(text)!;
    }
    const sanitized = sanitizeHtml(text);
    if (sanitizedTextCache.size > 1000) {
        const firstKey = sanitizedTextCache.keys().next().value;
        if (firstKey) sanitizedTextCache.delete(firstKey);
    }
    sanitizedTextCache.set(text, sanitized);
    return sanitized;
}

function getBoldenedString(text: string): string {
    const sanitized = getSanitizedText(text);
    const regex = searchRegex.value;
    if (!regex) return sanitized;
    return sanitized.replace(regex, '<b>$1</b>');
}

function onSearchSubmit() {
    if (filteredItems.value.length > 0) {
        emit('item-clicked', filteredItems.value[0]);
    }
}

function onEscape() {
    expandOptions.value = false;
    (document.activeElement as HTMLElement)?.blur();
}

function onItemClicked(item: any) {
    expandOptions.value = false;
    emit('item-clicked', item);
}
</script>
